import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "~/atoms/badge";
import { Checkbox } from "~/atoms/checkbox";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "~/atoms/card";
import { Skeleton } from "~/atoms/skeleton";
import { Table, TableBody, TableCell, TableRow } from "~/atoms/table";
import SimpleDataTable, { type TableColumn } from "~/molecules/SimpleDataTable";
import SearchBar from "~/molecules/SearchBar";
import { FilterPopover } from "~/molecules/FilterPopover";
import { type FilterWithOption } from "~/hooks/useFilter";
import { includesSearch } from "~/helpers/string";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
} from "~/atoms/empty";
import { FileText } from "lucide-react";
import { toggleSetItem } from "~/helpers/set";
import { printFullName } from "~/helpers/user";
import { useUserContext } from "~/hooks/useUserContext";
import { TagsValue } from "~/molecules/Tags";

const EMPTY_CELL = <span className="text-muted-foreground text-sm">-</span>;

export interface EligibleDocument {
  id: number;
  title: string;
  category: { id: number; title: string };
  tagIds: number[];
  currentTemplateName: string | null;
  currentTemplateId: number | null;
  owner: { userId: number; firstName: string; lastName?: string } | null;
  tier: string | null;
  reviewFrom: string | null;
  reviewTo: string | null;
}

interface Props {
  templateId: number | null;
  documents: EligibleDocument[];
  tagMap: Map<number, string>;
  isLoading: boolean;
  selectedIds: Set<number>;
  onSelectionChange: (ids: Set<number>) => void;
  actions?: React.ReactNode;
}

export function WorkflowTemplateDocumentsTable({
  templateId,
  documents,
  tagMap,
  isLoading,
  selectedIds,
  onSelectionChange,
  actions,
}: Props) {
  const { t } = useTranslation();
  const { user: currentUser } = useUserContext();

  const resolveTemplateName = useCallback(
    (name: string | null) => name ?? t(($) => $.workflowTemplates.defaultBadge),
    [t],
  );

  const [search, setSearch] = useState("");
  const [activeFilterValues, setActiveFilterValues] = useState<
    Record<string, string>
  >({});

  const filters: FilterWithOption<EligibleDocument>[] = useMemo(() => {
    const allOption = { value: "all", label: t(($) => $.commons.all) };

    const categoryOptions = new Map<number, string>();
    const tagOptions = new Map<number, string>();
    const templateOptions = new Map<string, string>();
    const tierOptions = new Map<string, string>();
    const ownerOptions = new Map<string, string>();
    templateOptions.set(
      "none",
      t(($) => $.workflowTemplates.documentsTable.none),
    );

    for (const doc of documents) {
      categoryOptions.set(doc.category.id, doc.category.title);

      for (const tagId of doc.tagIds) {
        tagOptions.set(tagId, tagMap.get(tagId) ?? String(tagId));
      }

      if (doc.currentTemplateId != null) {
        const displayName = resolveTemplateName(doc.currentTemplateName);
        templateOptions.set(String(doc.currentTemplateId), displayName);
      }

      if (doc.tier != null) {
        tierOptions.set(doc.tier, doc.tier);
      }

      if (doc.owner != null) {
        ownerOptions.set(
          String(doc.owner.userId),
          printFullName(doc.owner, doc.owner.userId === currentUser.id),
        );
      }
    }

    return [
      {
        id: "template",
        label: t(($) => $.workflowTemplates.documentsTable.currentTemplate),
        options: [
          allOption,
          ...[...templateOptions].map(([value, label]) => ({
            value,
            label,
          })),
        ],
        optionId: activeFilterValues.template ?? "all",
        getValue: (doc: EligibleDocument) =>
          doc.currentTemplateId != null
            ? String(doc.currentTemplateId)
            : "none",
      },
      {
        id: "tier",
        label: t(($) => $.commons.document.tier),
        options: [
          allOption,
          ...[...tierOptions].map(([value, label]) => ({
            value,
            label,
          })),
        ],
        optionId: activeFilterValues.tier ?? "all",
        getValue: (doc: EligibleDocument) => doc.tier ?? "",
      },
      {
        id: "category",
        label: t(($) => $.commons.document.category),
        options: [
          allOption,
          ...[...categoryOptions].map(([id, title]) => ({
            value: String(id),
            label: title,
          })),
        ],
        optionId: activeFilterValues.category ?? "all",
        getValue: (doc: EligibleDocument) => String(doc.category.id),
      },
      {
        id: "owner",
        label: t(($) => $.commons.document.owner),
        options: [
          allOption,
          ...[...ownerOptions].map(([value, label]) => ({
            value,
            label,
          })),
        ],
        optionId: activeFilterValues.owner ?? "all",
        getValue: (doc: EligibleDocument) =>
          doc.owner != null ? String(doc.owner.userId) : "",
      },
      {
        id: "tag",
        label: t(($) => $.commons.document.tags),
        options: [
          allOption,
          ...[...tagOptions].map(([id, title]) => ({
            value: String(id),
            label: title,
          })),
        ],
        optionId: activeFilterValues.tag ?? "all",
        getValue: (doc: EligibleDocument) => doc.tagIds.map((id) => String(id)),
      },
    ];
  }, [
    documents,
    tagMap,
    activeFilterValues,
    resolveTemplateName,
    currentUser.id,
    t,
  ]);

  const handleSelectFilter = (filterId: string, optionId: string) => {
    setActiveFilterValues((prev) => {
      const next = { ...prev };
      if (optionId === "all") {
        delete next[filterId];
      } else {
        next[filterId] = optionId;
      }
      return next;
    });
  };

  const handleClearFilters = () => {
    setActiveFilterValues({});
  };

  const activeFiltersCount = Object.keys(activeFilterValues).length;

  const filteredDocuments = useMemo(() => {
    const activeFilters = filters.filter((f) => f.optionId !== "all");

    return documents.filter((doc) => {
      if (search && !includesSearch(doc.title, search)) return false;
      return activeFilters.every(({ optionId, getValue }) => {
        const value = getValue(doc);
        return Array.isArray(value)
          ? value.some((v) => v === optionId)
          : value === optionId;
      });
    });
  }, [documents, search, filters]);

  const toggleSelection = useCallback(
    (versionId: number) => {
      onSelectionChange(toggleSetItem(selectedIds, versionId));
    },
    [selectedIds, onSelectionChange],
  );

  const toggleAll = useCallback(() => {
    if (selectedIds.size === filteredDocuments.length) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(filteredDocuments.map((d) => d.id)));
    }
  }, [selectedIds.size, filteredDocuments, onSelectionChange]);

  const allSelected =
    filteredDocuments.length > 0 &&
    selectedIds.size === filteredDocuments.length;

  const columns: TableColumn<EligibleDocument>[] = useMemo(
    () => [
      {
        header: (
          <Checkbox
            checked={allSelected}
            onCheckedChange={toggleAll}
            aria-label={t(($) => $.commons.buttons.selectAll)}
          />
        ),
        cell: (doc: EligibleDocument) => (
          <Checkbox
            checked={selectedIds.has(doc.id)}
            onCheckedChange={() => toggleSelection(doc.id)}
            aria-label={t(($) => $.commons.buttons.selectItem, {
              name: doc.title,
            })}
            onClick={(e: React.MouseEvent) => e.stopPropagation()}
          />
        ),
        tableHeadClassName: "w-12",
      },
      {
        header: t(($) => $.workflowTemplates.documentsTable.documentTitle),
        compareFn: (a: EligibleDocument, b: EligibleDocument) =>
          a.title.localeCompare(b.title),
        cell: (doc: EligibleDocument) => (
          <span className="font-medium">{doc.title}</span>
        ),
      },
      {
        header: t(($) => $.workflowTemplates.documentsTable.currentTemplate),
        defaultSort: true,
        compareFn: (a: EligibleDocument, b: EligibleDocument) => {
          const aMatch = a.currentTemplateId === templateId ? 1 : 0;
          const bMatch = b.currentTemplateId === templateId ? 1 : 0;
          if (aMatch !== bMatch) return bMatch - aMatch;
          return a.title.localeCompare(b.title);
        },
        cell: (doc: EligibleDocument) =>
          doc.currentTemplateId != null ? (
            <Badge
              variant={
                doc.currentTemplateId === templateId ? "default" : "outline"
              }
            >
              {resolveTemplateName(doc.currentTemplateName)}
            </Badge>
          ) : (
            EMPTY_CELL
          ),
      },
      {
        header: t(($) => $.commons.document.tier),
        compareFn: (a: EligibleDocument, b: EligibleDocument) =>
          (a.tier ?? "").localeCompare(b.tier ?? ""),
        cell: (doc: EligibleDocument) =>
          doc.tier ? <span>{doc.tier}</span> : EMPTY_CELL,
      },
      {
        header: t(($) => $.commons.document.category),
        compareFn: (a: EligibleDocument, b: EligibleDocument) =>
          a.category.title.localeCompare(b.category.title),
        cell: (doc: EligibleDocument) => <span>{doc.category.title}</span>,
      },
      {
        header: t(($) => $.commons.document.owner),
        compareFn: (a: EligibleDocument, b: EligibleDocument) => {
          const aName = a.owner ? printFullName(a.owner) : "";
          const bName = b.owner ? printFullName(b.owner) : "";
          return aName.localeCompare(bName);
        },
        cell: (doc: EligibleDocument) =>
          doc.owner ? (
            <span>
              {printFullName(doc.owner, doc.owner.userId === currentUser.id)}
            </span>
          ) : (
            EMPTY_CELL
          ),
      },
      {
        header: t(($) => $.commons.document.tags),
        cell: (doc: EligibleDocument) => (
          <div className="flex flex-wrap gap-1">
            {doc.tagIds.length === 0
              ? EMPTY_CELL
              : doc.tagIds.map((tagId) => (
                  <TagsValue key={tagId}>
                    {tagMap.get(tagId) ?? tagId}
                  </TagsValue>
                ))}
          </div>
        ),
      },
    ],

    [
      allSelected,
      selectedIds,
      templateId,
      tagMap,
      resolveTemplateName,
      currentUser.id,
      t,
      toggleAll,
      toggleSelection,
    ],
  );

  return (
    <Card>
      <CardHeader>
        <CardTitle>
          {t(($) => $.workflowTemplates.documentsTable.title)}
        </CardTitle>
        <CardDescription>
          {t(($) => $.workflowTemplates.documentsTable.description)}
        </CardDescription>
        {selectedIds.size > 0 && (
          <p className="text-muted-foreground text-sm">
            {t(($) => $.workflowTemplates.documentsTable.selectedCount, {
              count: selectedIds.size,
            })}
          </p>
        )}
        <div className="flex gap-2">
          <FilterPopover
            filters={filters}
            activeFiltersCount={activeFiltersCount}
            onClearFilters={handleClearFilters}
            onSelectFilter={handleSelectFilter}
          />
          <SearchBar
            placeholder={t(
              ($) => $.workflowTemplates.documentsTable.searchPlaceholder,
            )}
            value=""
            onSearch={setSearch}
            onDebouncedSearch={setSearch}
            onClearSearch={() => setSearch("")}
          />
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Table wrapperClassName="max-h-110 rounded-md border">
            <TableBody>
              {Array.from({ length: 5 }).map((_, i) => (
                <TableRow key={i}>
                  <TableCell className="w-12">
                    <Skeleton className="size-4" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-20 rounded-full" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-16" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-5 w-16 rounded-full" />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : filteredDocuments.length === 0 ? (
          <Empty className="h-fit border-0 py-8">
            <EmptyHeader>
              <EmptyMedia variant="icon">
                <FileText />
              </EmptyMedia>
              <EmptyDescription>
                {t(($) => $.workflowTemplates.documentsTable.noDocuments)}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        ) : (
          <SimpleDataTable
            columns={columns}
            data={filteredDocuments}
            onClickRow={(doc) => toggleSelection(doc.id)}
            wrapperClassName="max-h-110 rounded-md border"
          />
        )}
      </CardContent>
      {actions && <CardFooter>{actions}</CardFooter>}
    </Card>
  );
}
